import { useEffect, useRef } from 'react';
import { usePlayheadStore } from '../store/playheadStore';
import { useEditorStore } from '../store/editorStore';

/**
 * Auto-scroll the timeline during playback when playhead reaches 80% of visible area.
 * Disables on manual scroll (re-enables when playback restarts).
 */
export function useAutoScroll(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  const manualScroll = useRef(false);
  const wasPlaying = useRef(false);

  useEffect(() => {
    let rafId = 0;

    function tick() {
      const { playing, currentTimeMs } = usePlayheadStore.getState();

      // Reset manual scroll flag when playback starts
      if (playing && !wasPlaying.current) {
        manualScroll.current = false;
      }
      wasPlaying.current = playing;

      if (!playing || manualScroll.current) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
      const canvasWidth = canvas.getBoundingClientRect().width;
      const visibleDurationMs = canvasWidth / pxPerMs;
      const threshold = scrollOffsetMs + visibleDurationMs * 0.8;

      if (currentTimeMs > threshold) {
        // Scroll so playhead is at 20% from left
        const newOffset = currentTimeMs - visibleDurationMs * 0.2;
        useEditorStore.getState().setScrollOffset(Math.max(0, newOffset));
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [canvasRef]);

  // Detect manual scroll to disable auto-scroll
  const onManualScroll = () => {
    if (usePlayheadStore.getState().playing) {
      manualScroll.current = true;
    }
  };

  return { onManualScroll };
}
